import { factOf, valueOf } from '@asvise/facts'
import {
  DOMAIN_LABEL,
  HOUSEHOLD_FACT_REGISTRY,
  PERSON_FACT_REGISTRY,
  type Answers,
  type Assessment,
  type HouseholdFactField,
  type Person,
  type PersonFactField,
} from '@asvise/household-engine/vocabulary'
import { useEditing } from './editing'
import { MoneyField, NumberField, Question, RadioGroup } from './fields'

/**
 * One question per fact, as data rather than as a screen.
 *
 * The questionnaire used to be a page of hand-written questions in the order
 * somebody thought they should be asked, which works for one long form and
 * cannot answer the question the board asks: what are the four questions this
 * one exposure is waiting on. The engine already says which fields are missing
 * from a calculation, by name, so what was needed was a way to render a
 * question given a field name.
 *
 * TWO TABLES, AND THEY ARE `Record` ON PURPOSE. A field union with no entry
 * fails the build, which is the property the hand-written form could not have.
 * It caught one the moment it was written: `employer_cover_portable` is in the
 * registry, the engine reads it, and the form never asked it, so the branch
 * that says whether cover through work survives a job change could not fire
 * from the interface at all.
 *
 * The wording lives in the engine's registry, which is where the framework's
 * question numbers live too. What is here is how to collect an answer and when
 * it is worth asking at all.
 */

/**
 * Which protection areas an answer touches, by the engine's own account.
 *
 * Read out of the assessment rather than kept as a table, because the engine
 * names the fields each calculation reads and the fields it went without, and a
 * second mapping in a screen would be the one that disagrees with the
 * arithmetic.
 *
 * TWO QUESTIONS, NOT ONE. The sheet opens from an area that is waiting, so it
 * asks what an answer would unblock: `settles`, over `missing`. The long form
 * is read by somebody looking at every question at once, so it asks what each
 * one is for: `usedBy`, over `uses`. Deriving the form's line from `missing`
 * was the first attempt and it rendered nothing at all, because a household
 * that has answered everything has no missing field anywhere: the annotation
 * vanished exactly where the form is most used, which is for review.
 */
const areas = (
  assessment: Assessment | null,
  field: HouseholdFactField,
  within: (f: Assessment['findings'][number]) => HouseholdFactField[],
): string[] => {
  if (!assessment) return []
  return [
    ...new Set(
      assessment.findings.filter((f) => within(f).includes(field)).map((f) => DOMAIN_LABEL[f.domain]),
    ),
  ]
}

/** Areas whose calculation is held up for want of this answer. */
export const settles = (assessment: Assessment | null, field: HouseholdFactField): string[] =>
  areas(assessment, field, (f) => f.missing)

/**
 * Areas whose reasoning reads this answer, answered or not.
 *
 * The finding's own list rather than the calculation's, because four of the six
 * areas have no calculation: what a hospital policy leaves a household to find
 * is judged against its savings without ever producing a need, and reading the
 * estimate alone annotated two areas and left the other four blank.
 */
export const usedBy = (assessment: Assessment | null, field: HouseholdFactField): string[] =>
  /*
    Absent on a screening stored before the engine reported it, which is a real
    case rather than a defensive habit: the snapshots are immutable and stamped
    with the rule set that made them, so a row written last month will not grow
    the field and this will be read by code that has it. An older screening
    honestly does not know what its own calculations read, and says nothing
    rather than throwing the screen away.
  */
  areas(assessment, field, (f) => f.uses ?? [])

interface Choice {
  value: string
  label: string
}

type Spec =
  | { kind: 'money'; hint?: string }
  | { kind: 'number'; hint?: string; min?: number }
  | { kind: 'choice'; hint?: string; options: Choice[] }
  /** Stored as a fraction of one, typed as a percentage, because nobody types 0.35. */
  | { kind: 'percent'; hint?: string }

const YES_NO: Choice[] = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'UNKNOWN', label: 'Not sure' },
]

export const HOUSEHOLD_FIELDS: Record<HouseholdFactField, Spec> = {
  household_income_monthly: { kind: 'money' },
  essential_expenses_monthly: {
    kind: 'money',
    hint: 'Rent or mortgage, food, utilities, transport, loan repayments. Not insurance.',
  },
  existing_premiums_monthly: { kind: 'money' },
  other_spending_monthly: {
    kind: 'money',
    hint: 'Everything that is not in the two above. Asked because what is spare is what is left after what you actually spend, not after what you call essential.',
  },
  liquid_savings: { kind: 'money', hint: 'What you could reach within a few days without penalty.' },
  total_debt: { kind: 'money', hint: 'Mortgage, loans, anything outstanding.' },
  education_commitments: { kind: 'money' },
  income_stability: {
    kind: 'choice',
    options: [
      { value: 'STABLE', label: 'Steady' },
      { value: 'VARIABLE', label: 'It varies' },
      { value: 'SELF_EMPLOYED', label: 'Self-employed' },
      { value: 'CONTRACT', label: 'On contract' },
      { value: 'UNKNOWN', label: 'Not sure' },
    ],
  },
  sustainable_protection_budget: { kind: 'money' },
  recent_life_event: {
    kind: 'choice',
    options: [
      { value: 'NONE', label: 'Nothing recently' },
      { value: 'MARRIAGE', label: 'Married' },
      { value: 'CHILDBIRTH', label: 'A new child' },
      { value: 'HOME_PURCHASE', label: 'Bought a home' },
      { value: 'JOB_CHANGE', label: 'Changed job' },
      { value: 'CAREGIVING', label: 'Started caring for someone' },
      { value: 'BEREAVEMENT', label: 'A bereavement' },
    ],
  },
  support_horizon_years: {
    kind: 'number',
    min: 0,
    hint: 'If the income stopped, how many years should the family be able to keep going?',
  },
  survivor_expenses_monthly: {
    kind: 'money',
    hint: "Usually less than today's essentials: a debt settled from a benefit is no longer repaid monthly.",
  },
  recovery_period_months: {
    kind: 'number',
    min: 0,
    hint: 'If someone could not work while recovering, how many months should the family be able to fund?',
  },
  recovery_additional_costs: {
    kind: 'money',
    hint: 'Help at home, travel, work not done. Not the medical bill.',
  },
  savings_allocated_to_protection: {
    kind: 'money',
    hint: 'Only what you would put towards this. Money kept for emergencies cannot answer this as well, and nought is a real answer.',
  },
  final_and_transition_costs: { kind: 'money' },
}

/**
 * The per-member questions, and which of them are worth asking.
 *
 * `when` is framework section 4.1's progressive disclosure, stated per field
 * rather than as nesting in a screen, so the board and the sheet obey it
 * without either of them knowing why.
 */
interface PersonSpec {
  spec: Spec
  when?: (person: Person) => boolean
}

const earns = (person: Person) => valueOf(person.facts.earns) === 'YES'

/** Only where somebody has said they hold cover through work at all. */
const hasWorkCover = (person: Person) =>
  valueOf(person.facts.employer_life_cover) === 'YES' ||
  valueOf(person.facts.employer_medical_cover) === 'YES'

export const PERSON_FIELDS: Record<PersonFactField, PersonSpec> = {
  birth_year: {
    spec: { kind: 'number', min: 1900, hint: 'The year is enough. We never ask for a date of birth.' },
  },
  earns: { spec: { kind: 'choice', options: YES_NO } },
  income_share: {
    spec: {
      kind: 'percent',
      hint: "Roughly what share of the family's income they bring in. One person earning everything is 100.",
    },
    when: earns,
  },
  has_dependants: { spec: { kind: 'choice', options: YES_NO } },
  residency: {
    spec: {
      kind: 'choice',
      options: [
        { value: 'CITIZEN_OR_PR', label: 'Singapore citizen or permanent resident' },
        { value: 'OTHER', label: 'Neither' },
        { value: 'UNKNOWN', label: 'Not sure' },
      ],
    },
  },
  employer_life_cover: { spec: { kind: 'choice', options: YES_NO }, when: earns },
  employer_medical_cover: { spec: { kind: 'choice', options: YES_NO }, when: earns },
  employer_cover_portable: {
    /**
     * The field the form never asked. The engine reads it to decide how to
     * describe cover through work, and treats an unanswered question the same
     * way as a no, so never asking meant the only wording anybody could ever
     * see was the cautious one.
     */
    spec: {
      kind: 'choice',
      hint: 'Treated as ending with the job unless the documents say otherwise, so "not sure" is read the same way as "no".',
      options: [
        { value: 'YES', label: 'Yes, the documents say it continues' },
        { value: 'NO', label: 'No, it ends with the job' },
        { value: 'UNKNOWN', label: 'Not sure' },
      ],
    },
    when: hasWorkCover,
  },
}

/** Whether a member's question is worth putting in front of anybody. */
export function asksPerson(person: Person, field: PersonFactField): boolean {
  const when = PERSON_FIELDS[field].when
  return when === undefined || when(person)
}

function Input({
  spec,
  name,
  value,
  onChange,
}: {
  spec: Spec
  name: string
  value: unknown
  onChange: (next: unknown) => void
}) {
  if (spec.kind === 'choice') {
    return (
      <RadioGroup
        name={name}
        value={(value as string) ?? null}
        onChange={onChange}
        options={spec.options}
      />
    )
  }

  if (spec.kind === 'money') {
    return (
      <MoneyField field={name} value={(value as number) ?? null} onChange={onChange} />
    )
  }

  if (spec.kind === 'percent') {
    const share = value as number | null
    return (
      <NumberField
        field={name}
        value={share === null || share === undefined ? null : Math.round(share * 100)}
        onChange={(v) => onChange(v === null ? null : v / 100)}
        min={0}
      />
    )
  }

  return (
    <NumberField
      field={name}
      value={(value as number) ?? null}
      onChange={onChange}
      {...(spec.min === undefined ? {} : { min: spec.min })}
    />
  )
}

/**
 * One household question, by field name.
 *
 * What the answer is for is said where the questions are listed rather than
 * here. A sheet is opened from the one thing that wanted it and says so at the
 * foot; the record is the screen where somebody is looking at twenty questions
 * and cannot tell what any of them feeds.
 */
export function HouseholdQuestion({
  field,
  answers,
  onChange,
}: {
  field: HouseholdFactField
  answers: Answers
  onChange: (next: Answers) => void
}) {
  const editing = useEditing()
  const spec = HOUSEHOLD_FIELDS[field]

  /**
   * Every change tells the settle window it happened, which is what stops a
   * half-typed figure being recorded. The signal is movement rather than focus:
   * a field somebody is sitting in without typing would otherwise be withheld
   * for as long as the cursor is there.
   */
  const set = (value: unknown) => {
    editing.moved?.(field)
    onChange({
      ...answers,
      [field]: value === null ? null : factOf(value as never, { source: 'USER' }),
    })
  }

  return (
    <Question
      title={HOUSEHOLD_FACT_REGISTRY[field].label}
      {...(spec.hint === undefined ? {} : { hint: spec.hint })}
    >
      <Input spec={spec} name={field} value={valueOf(answers[field] as never)} onChange={set} />
    </Question>
  )
}

/**
 * One answer about one member, written into the household.
 *
 * Here rather than in the screen that happens to be showing, because there are
 * two of them now: the record and the sheet the record opens over it. The
 * settle window is the part that cannot be left to each caller. It withholds a
 * field somebody is still moving in, and a screen that wrote a member's fact
 * without saying so recorded a half-typed year of birth.
 */
export function withPersonFact(
  answers: Answers,
  id: string,
  field: PersonFactField,
  value: unknown,
): Answers {
  return {
    ...answers,
    people: answers.people.map((p) =>
      p.id === id
        ? {
            ...p,
            facts: {
              ...p.facts,
              [field]: value === null ? null : factOf(value as never, { source: 'USER' }),
            },
          }
        : p,
    ),
  }
}

/** One question about one member, by field name. */
export function PersonQuestion({
  person,
  field,
  answers,
  onChange,
}: {
  person: Person
  field: PersonFactField
  answers: Answers
  onChange: (next: Answers) => void
}) {
  const editing = useEditing()
  const { spec } = PERSON_FIELDS[field]
  const name = `${person.id}:${field}`

  return (
    <Question
      title={PERSON_FACT_REGISTRY[field].label}
      {...(spec.hint === undefined ? {} : { hint: spec.hint })}
    >
      <Input
        spec={spec}
        name={name}
        value={valueOf(person.facts[field] as never)}
        onChange={(v) => {
          editing.moved?.(name)
          onChange(withPersonFact(answers, person.id, field, v))
        }}
      />
    </Question>
  )
}

/**
 * An answer as a record prints it, from the same spec that collected it.
 *
 * The business app writes this as a switch over its fact fields, which is the
 * one place its record can disagree with its form: an option renamed in the
 * question shows its old label in the row. Reading the spec means the row shows
 * whatever the question offered, and a new choice needs nothing said here.
 *
 * Null is "not known", and the row says so. It is never an empty string, which
 * on a row of label and value reads as a value of nothing.
 */
function printed(spec: Spec, value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (spec.kind === 'money') return `S$${(value as number).toLocaleString('en-SG')}`
  if (spec.kind === 'percent') return `${Math.round((value as number) * 100)}%`
  if (spec.kind === 'choice') {
    return spec.options.find((o) => o.value === value)?.label ?? String(value)
  }
  return String(value)
}

export const answerOf = (field: HouseholdFactField, answers: Answers): string | null =>
  printed(HOUSEHOLD_FIELDS[field], valueOf(answers[field] as never))

export const personAnswerOf = (person: Person, field: PersonFactField): string | null =>
  printed(PERSON_FIELDS[field].spec, valueOf(person.facts[field] as never))
